import { getAPI, postAPI, deleteAPI, postMultipartAPI } from "./Networking";
import {
  InventoryListingDetails,
  ProjectInventoryOpsProductDetails,
  ProjectInventoryOpsSummary,
} from "../models/ProjectManagement/ProjectInventoryProduct";
import {
  PrintInwardDetailsReq,
  PrintInwardQcReq,
  PrintInwardReq,
  PrintInwardTimelineReq,
  PrintOutwardReq,
  RawInwardDetailsReq,
  RawInwardQcReq,
  RawInwardReq,
  RawInwardTimelineReq,
  VendorInvoice,
  VendorInvoiceType,
} from "../models/ProjectManagement/InventoryOpsRequests";

export default class ProjectInventoryOpsAPI {
  getInventoryOpsProducts = async (
    projectId: number
  ): Promise<InventoryListingDetails> => {
    try {
      const response = await getAPI(
        `/v5/projects/inventory-ops/id/${projectId}`
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  getProjectInventoryOpsProductDetails = async (
    projectId: number,
    productId: number
  ): Promise<ProjectInventoryOpsProductDetails> => {
    try {
      const response = await getAPI(
        `/v5/projects/inventory-ops/id/${projectId}/${productId}`
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  updateRawInward = async (
    reqBody: RawInwardReq
  ): Promise<Partial<ProjectInventoryOpsProductDetails>> => {
    try {
      const response = await postAPI(
        `/v5/projects/inventory-ops/id/update-raw-inward`,
        reqBody
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  updateRawInwardQc = async (
    body: RawInwardQcReq
  ): Promise<Partial<ProjectInventoryOpsProductDetails>> => {
    try {
      const response = await postAPI(
        `/v5/projects/inventory-ops/id/update-raw-inward-qc`,
        body
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  updatePrintOutward = async (
    body: PrintOutwardReq
  ): Promise<Partial<ProjectInventoryOpsProductDetails>> => {
    try {
      const response = await postAPI(
        `/v5/projects/inventory-ops/id/print-outward`,
        body
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  updatePrintInward = async (
    body: PrintInwardReq
  ): Promise<Partial<ProjectInventoryOpsProductDetails>> => {
    try {
      const response = await postAPI(
        `/v5/projects/inventory-ops/id/print-inward`,
        body
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  updatePrintInwardQc = async (
    body: PrintInwardQcReq
  ): Promise<Partial<ProjectInventoryOpsProductDetails>> => {
    try {
      const response = await postAPI(
        `/v5/projects/inventory-ops/id/print-inward-qc`,
        body
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  updateRawInwardExpectedDate = async (
    body: RawInwardTimelineReq
  ): Promise<void> => {
    try {
      const response = await postAPI(
        `/v5/projects/inventory-ops/id/update-pr-expected-date`,
        body
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  updatePrintInwardTimeline = async (
    body: PrintInwardTimelineReq
  ): Promise<void> => {
    try {
      const response = await postAPI(
        `/v5/projects/inventory-ops/id/update-print-inward-timeline`,
        body
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  startRawInwardQc = async (body: RawInwardQcReq): Promise<void> => {
    try {
      const response = await postAPI(
        `/v5/projects/inventory-ops/id/start-raw-inward-qc`,
        body
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  startPrintInwardQc = async (body: PrintInwardQcReq): Promise<void> => {
    try {
      const response = await postAPI(
        `/v5/projects/inventory-ops/id/start-print-inward-qc`,
        body
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  getSummary = async (
    projectId: number,
    productId: number
  ): Promise<ProjectInventoryOpsSummary> => {
    try {
      const response = await getAPI(
        `/v5/projects/inventory-ops/id/${projectId}/${productId}/summary`
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  deleteRawInwardQCImg = async (id: number): Promise<any> => {
    try {
      const response = await deleteAPI(
        `/v5/projects/inventory-ops/id/raw-inward-qc-image/${id}`
      );

      return response;
    } catch (error) {
      throw error;
    }
  };
  deletePrintInwardQCImg = async (id: number): Promise<any> => {
    try {
      const response = await deleteAPI(
        `/v5/projects/inventory-ops/id/print-inward-qc-image/${id}`
      );

      return response;
    } catch (error) {
      throw error;
    }
  };

  updateRawInwardDetails = async (
    reqBody: RawInwardDetailsReq
  ): Promise<Partial<ProjectInventoryOpsProductDetails>> => {
    try {
      const response = await postAPI(
        `/v5/projects/inventory-ops/id/update-raw-inward-details`,
        reqBody
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  updatePrintInwardDetails = async (
    body: PrintInwardDetailsReq
  ): Promise<Partial<ProjectInventoryOpsProductDetails>> => {
    try {
      const response = await postAPI(
        `/v5/projects/inventory-ops/id/update-print-inward-details`,
        body
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  fetchVendorInvoices = async (
    inwardId: number,
    type: VendorInvoiceType
  ): Promise<any> => {
    try {
      const response = await getAPI(
        `/v5/projects/inventory-ops/id/${inwardId}/type/${type}/invoices`
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  addUpdateVendorInvoices = async (
    inwardId: number,
    type: VendorInvoiceType,
    body: VendorInvoice[]
  ): Promise<any> => {
    try {
      // If any invoice contains files, send as multipart form data
      const hasFiles = body.some(
        (inv) => (inv as any).files && (inv as any).files?.length > 0
      );
      if (hasFiles) {
        const formData = new FormData();
        // Append a JSON representation of invoices without File objects
        const invoicesWithoutFiles = body.map((inv) => {
          const { files, ...rest } = inv as any;
          return rest;
        });
        formData.append("invoices", JSON.stringify(invoicesWithoutFiles));

        // Append files keyed by invoice_number when available, else by index
        body.forEach((inv, idx) => {
          const files = (inv as any).files as File[] | undefined | null;
          if (files && files.length > 0) {
            const keyBase = (inv as any).invoice_number
              ? String((inv as any).invoice_number)
              : String(idx);
            files.forEach((file) => {
              formData.append(`files[${keyBase}]`, file);
            });
          }
        });

        const response = await postMultipartAPI(
          `/v5/projects/inventory-ops/id/${inwardId}/type/${type}/invoices`,
          formData
        );
        return response.result;
      } else {
        const formData = new FormData();
        formData.append("invoices", JSON.stringify(body));

        const response = await postAPI(
          `/v5/projects/inventory-ops/id/${inwardId}/type/${type}/invoices`,
          formData
        );
        return response.result;
      }
    } catch (error) {
      throw error;
    }
  };

  updateInvoiceMarkedAsReceived = async (
    inwardId: number,
    type: VendorInvoiceType,
    markedAsReceived: boolean
  ): Promise<void> => {
    try {
      const response = await postAPI(
        `/v5/projects/inventory-ops/id/${inwardId}/type/${type}/mark-received`,
        { mark_received: markedAsReceived }
      );
      return;
    } catch (error) {
      throw error;
    }
  };
}
